/**
 * 답변 톤 관리 — 말투(VOICE) 라이브러리 전용.
 * 카테고리/도구별 톤 적용은 AI도구 화면에서 처리(이 화면은 톤 정의 관리만).
 * (design-20260604-answer-tone-format-orthogonal §4 상세화)
 */
import { useEffect, useState } from 'react';
import './AiAnswerFormat.css';
import {
  useStyleConfig, TONE_PRESETS,
  ToneVizSample, StyleSaveBar, Field, AiGeneratePanel,
} from './AiAnswerStyle.shared';
import { ConsoleScreenHead } from '../../../components/console-list/ConsoleScreenHead';

type Sel = { kind: 'tone'; key: string } | null;

export function AiAnswerToneSection() {
  const api = useStyleConfig();
  const { fmt, loading, update } = api;
  const [sel, setSel] = useState<Sel>(null);

  const toneKeys = fmt ? Object.keys(fmt.tones) : [];

  useEffect(() => {
    if (!sel && toneKeys.length) setSel({ kind: 'tone', key: toneKeys[0]! });
  }, [sel, toneKeys]);

  if (loading) return <div className="settings-loading">불러오는 중…</div>;
  if (!fmt) return <div className="settings-error">설정을 불러오지 못했습니다.</div>;

  const toneUsage = (key: string) => Object.values(fmt.style_by_tool ?? {}).filter((m) => m.tone === key).length;

  return (
    <div className="aiaf-section svc-cat svc-cat--ai-tone">
      <ConsoleScreenHead title="답변 톤" kpi={`${toneKeys.length}개`} desc="말투·어조 톤 라이브러리를 관리합니다." />
      <p className="settings-section-desc" style={{ margin: '0 0 14px' }}>
        AI 답변의 <b>말투·어조</b> 톤을 정의·관리합니다. 왼쪽에서 톤을 선택하면 오른쪽에 편집·시각화·예제가 표시됩니다.
        카테고리·도구별 톤 적용은 <b>AI 도구</b> 화면에서, 구조·표시형식은 <b>답변 포맷</b> 메뉴에서 설정합니다.
      </p>

      {api.hasLegacy && (
        <div className="aiaf-migrate" role="status">
          기존(구버전) 설정이 있습니다. 톤·포맷 2축으로 자동 변환할 수 있습니다.
          <button type="button" className="settings-btn" onClick={() => api.importLegacy()}>기존 설정 가져오기</button>
        </div>
      )}

      {/* 공통 말투 */}
      <div className="aiaf-common">
        <div className="aiaf-common__title">공통 말투 (전 카테고리 공통)</div>
        <div className="aiaf-common__row">
          <label>언어
            <input className="aiaf-input aiaf-input--sm" value={fmt.common?.language ?? 'ko'} maxLength={8}
              onChange={(e) => update((f) => { f.common = { ...f.common, language: e.target.value }; })} />
          </label>
          <label className="aiaf-fld--grow">금지 표현 (한 줄에 하나)
            <textarea className="aiaf-textarea" rows={2}
              value={(fmt.common?.forbid_phrases ?? []).join('\n')} placeholder={'추측\n아마도'}
              onChange={(e) => update((f) => { f.common = { ...f.common, forbid_phrases: e.target.value.split('\n').map((s) => s.trim()).filter(Boolean) }; })} />
          </label>
        </div>
      </div>

      <div className="aiaf-2pane">
        {/* 좌: 톤 라이브러리 */}
        <div className="aiaf-2pane__list">
          <div className="aiaf-listgroup">
            <div className="aiaf-listgroup__title">톤 라이브러리</div>
            {toneKeys.length === 0 && <p className="aiaf-empty">톤이 없습니다. 아래에서 추가하세요.</p>}
            {toneKeys.map((key) => {
              const t = fmt.tones[key]!;
              const on = sel?.kind === 'tone' && sel.key === key;
              return (
                <button key={key} type="button" className={`aiaf-litem${on ? ' is-sel' : ''}`} onClick={() => setSel({ kind: 'tone', key })}>
                  <span className="aiaf-litem__name">{t.description || key}</span>
                  <span className="aiaf-litem__meta">
                    <span>{({ formal: '정중', neutral: '중립', friendly: '친근' } as Record<string, string>)[t.formality ?? 'neutral']}</span>
                    <span className="aiaf-litem__use">· {toneUsage(key)}곳</span>
                  </span>
                </button>
              );
            })}
            <div className="aiaf-listgroup__add">
              <select className="aiaf-select aiaf-select--sm" value="" onChange={(e) => { if (e.target.value) { const k = api.addToneFromPreset(e.target.value); setSel({ kind: 'tone', key: k }); } e.currentTarget.value = ''; }}>
                <option value="">+ 프리셋에서</option>
                {TONE_PRESETS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
              </select>
              <button type="button" className="settings-btn" onClick={() => { const k = api.addCustomTone(); setSel({ kind: 'tone', key: k }); }}>+ 직접</button>
            </div>
            <AiGeneratePanel kind="tone" onAccept={(e) => { const k = api.addToneEntry(e); setSel({ kind: 'tone', key: k }); }} />
          </div>
        </div>

        {/* 우: 상세(편집·시각화·예제) */}
        <div className="aiaf-2pane__detail">
          {sel?.kind === 'tone' && fmt.tones[sel.key] ? (() => {
            const key = sel.key; const t = fmt.tones[key]!;
            return (
              <div>
                <div className="aiaf-detail__head">
                  <span className="aiaf-detail__title">{t.description || key}</span>
                  <span className="aiaf-detail__key">{key}</span>
                  <button type="button" className="aiaf-detail__del" onClick={() => { api.removeTone(key); setSel(null); }}>삭제</button>
                </div>
                <div className="aiaf-cat__row">
                  <Field label="이름">
                    <input className="aiaf-input aiaf-input--sm" value={t.description ?? ''} placeholder="톤 이름"
                      onChange={(e) => api.editTone(key, (x) => { x.description = e.target.value; })} />
                  </Field>
                  <Field label="격식">
                    <select className="aiaf-select" value={t.formality ?? 'neutral'}
                      onChange={(e) => api.editTone(key, (x) => { x.formality = e.target.value as 'formal' | 'neutral' | 'friendly'; })}>
                      <option value="formal">정중</option><option value="neutral">보통</option><option value="friendly">친근</option>
                    </select>
                  </Field>
                  <Field label="최대 문장 수">
                    <input className="aiaf-input aiaf-input--num" type="number" min={1} max={50} value={t.max_sentences ?? ''} placeholder="무제한"
                      onChange={(e) => api.editTone(key, (x) => { x.max_sentences = e.target.value === '' ? null : Number(e.target.value); })} />
                  </Field>
                </div>
                <label className="aiaf-instr">말투 지침 (한 줄에 하나)
                  <textarea className="aiaf-textarea" rows={Math.max(2, t.instructions.length)} value={t.instructions.join('\n')}
                    onChange={(e) => api.editTone(key, (x) => { x.instructions = e.target.value.split('\n').map((s) => s.trimEnd()); })} />
                </label>
                <div className="aiaf-detail__sec"><div className="aiaf-detail__sec-lbl">시각화</div><ToneVizSample tone={t} /></div>
                <p className="aiaf-detail__key">이 톤을 사용하는 카테고리·도구: {toneUsage(key)}곳 (AI 도구 화면에서 지정)</p>
              </div>
            );
          })() : (
            <div className="aiaf-detail__empty">왼쪽에서 톤을 선택하세요.</div>
          )}
        </div>
      </div>

      <StyleSaveBar api={api} label="톤 설정 저장" />
    </div>
  );
}
